import { http, HttpResponse, type JsonBodyType, type RequestHandler } from 'msw';

import { DemoProfile } from '../types';

import buildSamplePdf from './samplePdf';

const SIGNATURE_QUEUE_PATH = /\/api\/payroll\/v1\/documents\/signature-queue\/$/;
const DOCUMENT_LIST_PATHS = /\/api\/payroll\/v1\/documents\/(library\/)?$/;

interface KnownDocument {
    display_name: string;
    worker_name: string;
    tax_year: number | null;
    tax_quarter: number | null;
}

/** A form Gusto's embedded signer asks for, as the signature queue names it. */
interface SigningForm {
    documentUuid: string;
    title: string;
    companyId: string;
    employeeId: string | null;
    taxYear: number | null;
    taxQuarter: number | null;
}

interface QueueItem extends KnownDocument {
    document_uuid: string;
    signing?: { form_id: string | null; company_id: string; employee_id: string | null };
}

interface DocumentRow {
    document_uuid: string;
    requires_signature?: boolean;
    signed_at?: string | null;
    /** Library rows only. */
    signature_status?: string;
}

export interface DemoDocuments {
    handlers: RequestHandler[];
    /** Fed every payroll response, so a file and a Gusto form can be titled by the row that listed it. */
    remember: (body: unknown) => void;
    /** Shows what was signed this session: gone from the queue, signed in the list. */
    applySignatures: (path: string, body: JsonBodyType) => JsonBodyType;
}

/** What each form is for, as Gusto's signer lists it. Matched on the queue's display name. */
const FORM_DESCRIPTIONS: [RegExp, string][] = [
    [/8655/, 'Authorizes Gusto to file and pay payroll taxes on your behalf.'],
    [/W-4/, "Employee's Withholding Certificate for federal income tax."],
    [/Direct Deposit/i, 'Authorizes paychecks to be deposited into your bank account.'],
    [/I-9/, 'Employment Eligibility Verification.'],
];

const describe = (title: string | undefined) =>
    FORM_DESCRIPTIONS.find(([pattern]) => title && pattern.test(title))?.[1] ?? 'Review and sign this form.';

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const periodOf = ({ tax_year: year, tax_quarter: quarter }: KnownDocument) => {
    if (year && quarter) {
        return `Q${quarter} ${year}`;
    }
    return year ? `Tax year ${year}` : null;
};

/**
 * The Documents page's two halves. Viewing: every document opens a PDF titled for that document.
 * Signing: Gusto's embedded signer runs against mocked Gusto endpoints, and what it signs leaves the
 * queue, so the whole loop can be clicked through.
 */
export default function buildDocumentHandlers(profile: DemoProfile): DemoDocuments {
    const documents = new Map<string, KnownDocument>();
    const forms = new Map<string, SigningForm>();
    const signedDocuments = new Map<string, string>();

    const remember = (body: unknown): void => {
        if (Array.isArray(body)) {
            body.forEach(remember);
            return;
        }
        if (!isRecord(body)) {
            return;
        }
        const row = body as unknown as QueueItem;
        if (typeof row.document_uuid === 'string' && typeof row.display_name === 'string') {
            documents.set(row.document_uuid, row);
            if (row.signing?.form_id) {
                forms.set(row.signing.form_id, {
                    documentUuid: row.document_uuid,
                    title: row.display_name,
                    companyId: row.signing.company_id,
                    employeeId: row.signing.employee_id,
                    taxYear: row.tax_year,
                    taxQuarter: row.tax_quarter,
                });
            }
        }
        Object.values(body).forEach(remember);
    };

    const applySignatures = (path: string, body: JsonBodyType): JsonBodyType => {
        const page = body as { count: number; results: DocumentRow[] };
        if (SIGNATURE_QUEUE_PATH.test(path)) {
            const results = page.results.filter((row) => !signedDocuments.has(row.document_uuid));
            return { ...page, count: results.length, results };
        }
        if (DOCUMENT_LIST_PATHS.test(path)) {
            const signed = (row: DocumentRow): DocumentRow => ({
                ...row,
                requires_signature: false,
                signed_at: signedDocuments.get(row.document_uuid),
                ...(row.signature_status && { signature_status: 'signed' }),
            });
            return {
                ...page,
                results: page.results.map((row) => (signedDocuments.has(row.document_uuid) ? signed(row) : row)),
            };
        }
        return body;
    };

    /** Gusto's `Form`: https://docs.gusto.com/embedded-payroll/reference/get-v1-form */
    const gustoForm = (formId: string) => {
        const form = forms.get(formId);
        return {
            uuid: formId,
            employee_uuid: form?.employeeId ?? undefined,
            name: form?.title ?? 'Form',
            title: form?.title ?? 'Form',
            description: describe(form?.title),
            draft: false,
            year: form?.taxYear ?? null,
            quarter: form?.taxQuarter ?? null,
            requires_signing: !(form && signedDocuments.has(form.documentUuid)),
            document_content_type: 'application/pdf',
        };
    };

    const formsWhere = (predicate: (form: SigningForm) => boolean) =>
        [...forms.entries()].filter(([, form]) => predicate(form)).map(([formId]) => gustoForm(formId));

    const sign = (formId: string) => {
        const form = forms.get(formId);
        if (form) {
            signedDocuments.set(form.documentUuid, new Date().toISOString());
        }
        return HttpResponse.json(gustoForm(formId));
    };

    // `document_url` is only ever a "view the form" link in the signer, and a new tab can't reach
    // MSW, so it is left out rather than pointed somewhere that won't open.
    const formPdf = (formId: string) => HttpResponse.json({ uuid: formId, document_url: null });

    const sampleFile = (documentUuid: string) => {
        const document = documents.get(documentUuid);
        const title = document?.display_name ?? 'Payroll document';
        const period = document && periodOf(document);
        const pdf = buildSamplePdf([
            { text: title, size: 22 },
            { text: profile.organization, size: 13 },
            { text: document?.worker_name ? `Prepared for ${document.worker_name}` : 'Company document', size: 11 },
            ...(period ? [{ text: period, size: 11 }] : []),
            { text: '', size: 11 },
            { text: 'Sample document for the Collective demo.', size: 10 },
            { text: 'It contains no real payroll or tax information.', size: 10 },
        ]);
        return HttpResponse.arrayBuffer(pdf.buffer as ArrayBuffer, { headers: { 'Content-Type': 'application/pdf' } });
    };

    const handlers: RequestHandler[] = [
        // The viewer streams the file itself from our API, so this answers with the PDF directly.
        http.get('*/api/payroll/v1/documents/:documentUuid/content/', ({ params }) =>
            sampleFile(params.documentUuid as string)
        ),

        http.get('*/gusto/sdk/v1/forms/:formId', ({ params }) => HttpResponse.json(gustoForm(params.formId as string))),
        http.get('*/gusto/sdk/v1/forms/:formId/pdf', ({ params }) => formPdf(params.formId as string)),
        http.put('*/gusto/sdk/v1/forms/:formId/sign', ({ params }) => sign(params.formId as string)),
        http.get('*/gusto/sdk/v1/companies/:companyId/forms', ({ params }) =>
            HttpResponse.json(formsWhere((form) => form.companyId === params.companyId && !form.employeeId))
        ),
        http.get('*/gusto/sdk/v1/employees/:employeeId/forms', ({ params }) =>
            HttpResponse.json(formsWhere((form) => form.employeeId === params.employeeId))
        ),
        http.get('*/gusto/sdk/v1/employees/:employeeId/forms/:formId', ({ params }) =>
            HttpResponse.json(gustoForm(params.formId as string))
        ),
        http.get('*/gusto/sdk/v1/employees/:employeeId/forms/:formId/pdf', ({ params }) =>
            formPdf(params.formId as string)
        ),
        http.put('*/gusto/sdk/v1/employees/:employeeId/forms/:formId/sign', ({ params }) =>
            sign(params.formId as string)
        ),
        // The only employee the signer opens is the owner, who is the demo persona.
        http.get('*/gusto/sdk/v1/employees/:employeeId', ({ params }) =>
            HttpResponse.json({
                uuid: params.employeeId,
                first_name: profile.first_name,
                last_name: profile.last_name,
                email: profile.email,
                payment_method: 'Direct Deposit',
            })
        ),
        // The owner signs a W-4 and a direct deposit form, no I-9. The signer reads a 404 here as
        // "no I-9 authorization on file", which is the truth for this persona.
        http.get('*/gusto/sdk/v1/employees/:employeeId/i9_authorization', () =>
            HttpResponse.json({ errors: [{ error_key: 'employee_id', category: 'not_found' }] }, { status: 404 })
        ),
        // Anything else the SDK asks for gets a readable 404 rather than the demo's blanket 204,
        // which the SDK reports as an opaque content-type error.
        http.all('*/gusto/sdk/*', ({ request }) =>
            HttpResponse.json(
                {
                    errors: [
                        {
                            error_key: 'path',
                            category: 'not_found',
                            message: `Not mocked in the demo: ${new URL(request.url).pathname}`,
                        },
                    ],
                },
                { status: 404 }
            )
        ),
    ];

    return { handlers, remember, applySignatures };
}
